import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { ThemeProvider } from './lib/design-system';
import { isIosApp } from './lib/platform';
import Login from './pages/Login';
import Register from './pages/Register';
import TrainerRegister from './pages/TrainerRegister';
import VerifyEmail from './pages/VerifyEmail';
import ToastContainer from './components/ui/ToastContainer';
import Spinner from './components/ui/Spinner';

// Lazy load heavy dashboard components
const TrainerDashboard = lazy(() => import('./pages/TrainerDashboard'));
const PlanBuilderPage = lazy(() => import('./pages/PlanBuilderPage'));
const PlanViewPage = lazy(() => import('./pages/PlanViewPage'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const DesignSystemPreview = lazy(() => import('./pages/DesignSystemPreview'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const TrainerSettingsPage = lazy(() => import('./pages/TrainerSettingsPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Support = lazy(() => import('./pages/Support'));

function AppContent() {
  const { user, isInitialized, initAuth } = useAuthStore();

  useEffect(() => {
    initAuth();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  const LazyFallback = (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
      <Spinner size="lg" />
    </div>
  );

  return (
    <BrowserRouter>
      <Routes>
        {/* Design System Preview (accessible without auth for development) */}
        <Route path="/design-system" element={
          <Suspense fallback={LazyFallback}>
            <DesignSystemPreview />
          </Suspense>
        } />

        {/* Legal pages (public, no auth required) */}
        <Route path="/privacy" element={
          <Suspense fallback={LazyFallback}>
            <PrivacyPolicy />
          </Suspense>
        } />
        <Route path="/terms" element={
          <Suspense fallback={LazyFallback}>
            <TermsOfService />
          </Suspense>
        } />
        <Route path="/support" element={
          <Suspense fallback={LazyFallback}>
            <Support />
          </Suspense>
        } />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/trainer/register" element={<TrainerRegister />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route
          path="/trainer/plans/:id/edit"
          element={
            user?.role === 'TRAINER' || user?.role === 'ADMIN' ? (
              <Suspense fallback={LazyFallback}>
                <PlanBuilderPage />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/trainer/plans/:id"
          element={
            user?.role === 'TRAINER' || user?.role === 'ADMIN' ? (
              <Suspense fallback={LazyFallback}>
                <PlanViewPage />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Billing is web-only. On iOS the route redirects to settings, which shows
            the subscription status read-only (MOJ-48, Apple Guideline 3.1.1). */}
        <Route
          path="/trainer/billing"
          element={
            isIosApp() ? (
              <Navigate to="/trainer/settings" replace />
            ) : user?.role === 'TRAINER' || user?.role === 'ADMIN' ? (
              <Suspense fallback={LazyFallback}>
                <BillingPage />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/trainer/settings"
          element={
            user?.role === 'TRAINER' || user?.role === 'ADMIN' ? (
              <Suspense fallback={LazyFallback}>
                <TrainerSettingsPage />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/trainer/*"
          element={
            user?.role === 'TRAINER' || user?.role === 'ADMIN' ? (
              <Suspense fallback={LazyFallback}>
                <TrainerDashboard />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/customer/*"
          element={
            user?.role === 'CUSTOMER' ? (
              <Suspense fallback={LazyFallback}>
                <CustomerDashboard />
              </Suspense>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/"
          element={
            user ? (
              user.role === 'CUSTOMER' ? (
                <Navigate to="/customer" replace />
              ) : (
                <Navigate to="/trainer" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
